import { Stack, Text, Radio, Textarea, Chip, Group, Paper } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { useState } from 'react';
import { FlavorProfile } from '../../types/familyProfile';

type FlavorPreferenceProps = {
  value: { flavorProfile: FlavorProfile };
  onChange: (value: { flavorProfile: FlavorProfile }) => void;
};

export const FlavorPreference = ({ value, onChange }: FlavorPreferenceProps) => {
  const [preferencesText, setPreferencesText] = useState(value?.flavorProfile?.preferences || '');

  const flavorChips = [
    { label: 'Authentic', text: 'We prefer traditional, authentic homestyle recipes over Americanized versions' },
    { label: 'Fusion & modern', text: 'We enjoy fusion cuisines, Americanized versions, and modern twists on traditional recipes' },
    { label: 'Quick & simple', text: 'We value quick, simple, and unfussy meals over complex restaurant-style cooking (practical recipes rather than gourmet)' },
    { label: 'Gourmet', text: 'We love restaurant-quality flavors and techniques at home and reach for more gourmet recipes' },
    { label: 'Balanced', text: "We're all about quality ingredients and balance, not strict diets or rules, because healthy food shouldn't be boring" },
    { label: 'Health-conscious', text: 'We gravitate toward meals that are nutrient-rich and focused on whole foods, with an emphasis on fresh vegetables, lean proteins, and complex carbohydrates' },
    { label: 'Bold seasoning', text: "We appreciate seasoning that's anything but boring - the bolder, the better" },
    { label: 'Mild flavors', text: 'We favor gentle, subtle seasoning and mild flavors over bold or intense tastes' },
    { label: 'Moderate heat', text: 'We go for moderate spice levels - not too mild, not too hot' },
  ];

  const handleFlavorIntensityChange = (flavorIntensity: 'mild' | 'moderate' | 'bold') => {
    onChange({ flavorProfile: { ...value.flavorProfile, flavorIntensity } });
  };

  const handlePreferencesChange = (preferences: string) => {
    setPreferencesText(preferences);
    onChange({ flavorProfile: { ...value.flavorProfile, preferences: preferences.trim() || undefined } });
  };

  const handleChipClick = (chipText: string) => {
    const currentText = preferencesText.trim();
    
    // Check if this chip's text is already in the preferences
    if (currentText.includes(chipText)) {
      // Remove the chip text
      let newText = currentText.replace(chipText, '');
      // Clean up extra periods and spaces
      newText = newText.replace(/\.\s*\./g, '.').replace(/^\.\s*/, '').replace(/\s*\.\s*$/, '').trim();
      // Clean up multiple spaces
      newText = newText.replace(/\s+/g, ' ');
      handlePreferencesChange(newText);
    } else {
      // Add the chip text
      const newText = currentText 
        ? `${currentText}. ${chipText}`
        : chipText;
      handlePreferencesChange(newText);
    }
  };

  const isChipSelected = (chipText: string) => {
    return preferencesText.includes(chipText);
  };

  const getFlavorIntensityDescription = (intensity: string) => {
    switch (intensity) {
      case 'mild':
        return 'Simple seasonings, gentle flavors, comfort food approach';
      case 'moderate':
        return 'Well-seasoned with balanced, developed flavors';
      case 'bold':
        return 'Robust seasonings, complex flavor profiles, restaurant-quality depth';
      default:
        return '';
    }
  };

  const getMobileChips = () => flavorChips.filter(chip => ['Authentic', 'Fusion & modern', 'Quick & simple', 'Gourmet', 'Balanced', 'Health-conscious'].includes(chip.label));
  const getAllChips = () => flavorChips;

  return (
    <Stack gap="xl">
      {/* Flavor Intensity */}
      <Stack gap="md">
        <Text fw={500}>Flavor intensity</Text>
        
        <Stack gap="sm">
          {(['mild', 'moderate', 'bold'] as const).map((intensity) => (
            <Paper 
              key={intensity}
              p="md" 
              radius="lg" 
              style={{ 
                cursor: 'pointer',
                border: value.flavorProfile?.flavorIntensity === intensity ? '2px solid var(--mantine-color-orange-6)' : '1px solid var(--mantine-color-gray-3)',
                background: value.flavorProfile?.flavorIntensity === intensity ? 'var(--mantine-color-orange-0)' : 'white'
              }}
              onClick={() => handleFlavorIntensityChange(intensity)}
            >
              <Group gap="md" align="flex-start">
                <Radio
                  checked={value.flavorProfile?.flavorIntensity === intensity}
                  onChange={() => handleFlavorIntensityChange(intensity)}
                  color="orange"
                  size="md"
                />
                <Stack gap="xs" style={{ flex: 1 }}>
                  <Group gap="xs" align="center">
                    <Text fw={500} tt="capitalize">{intensity}</Text>
                    {intensity === 'bold' && <IconSparkles size={16} color="var(--mantine-color-orange-6)" />}
                  </Group>
                  <Text size="sm" c="dimmed">
                    {getFlavorIntensityDescription(intensity)}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))}
        </Stack>
      </Stack>

      {/* Additional Preferences */}
      <Stack gap="md">
        <Text fw={500}>Taste preferences</Text>
        
        <Text size="sm" c="dimmed">Select any that apply, or write your own:</Text>
        
        {/* Flavor Chips - Mobile (5 chips) */}
        <Group gap="xs" hiddenFrom="sm" wrap="wrap" className="chip-cloud">
          {getMobileChips().map((chip, index) => (
            <Chip
              key={index}
              checked={isChipSelected(chip.text)}
              onChange={() => handleChipClick(chip.text)}
              color="orange"
              size="sm"
              style={{ cursor: 'pointer', minWidth: 'fit-content' }}
            >
              {chip.label}
            </Chip>
          ))}
        </Group>

        {/* Flavor Chips - Desktop (all chips) */}
        <Group gap="xs" visibleFrom="sm" wrap="wrap" className="chip-cloud">
          {getAllChips().map((chip, index) => (
            <Chip
              key={index}
              checked={isChipSelected(chip.text)}
              onChange={() => handleChipClick(chip.text)}
              color="orange"
              size="sm"
              style={{ cursor: 'pointer', minWidth: 'fit-content' }}
            >
              {chip.label}
            </Chip>
          ))}
        </Group>
        
        <Textarea
          placeholder="Tell us what makes food delicious to your family..."
          value={preferencesText}
          onChange={(e) => handlePreferencesChange(e.target.value)}
          minRows={8}
          maxRows={15}
          maxLength={1000}
          size="md"
          styles={{
            input: {
              fontSize: '14px',
              lineHeight: 1.5,
              minHeight: '120px'
            }
          }}
        />
      </Stack>
    </Stack>
  );
};
